export type ReservationTimeSettings = {
  start_hour: number;
  start_minutes: number;
  desk_duration: number;
  meeting_room_duration: number;
};

export type ReservationTimeSettingsResponse = {
  organization_level_settings?: OfficeOrOrgSettings;
  office_level_settings?: OfficeOrOrgSettings;
};

export type OfficeOrOrgSettings = {
  reservation_default_time?: ReservationTimeSettings;
};

export enum OnlineMeetingIntegrations {
  MICROSOFT_ONLINE_MEETING_PROVIDERS = 'microsoft_online_meeting_providers',
  GOOGLE_ONLINE_MEETING_PROVIDERS = 'google_online_meeting_providers'
}

/**
 * Organization-selected map spot colors (map_theme_settings).
 * `rows` holds one color per status row of the spot color matrix
 * (available/approval/assigned/walkUp/unavailable/checkedIn); `overrides`
 * pins individual spot color tokens (keys of MAP_SPOT_COLORS) to a color.
 */
export type MapSpotColorsSettings = {
  rows?: Record<string, string>;
  overrides?: Record<string, string>;
};

/**
 * Day calendar weeks start on, following the date-fns convention:
 * 0=Sunday, 1=Monday, ..., 6=Saturday.
 */
export type WeekStartsOnDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** The map view's side-panel tabs, in their default order. */
export const MAP_TABS = ["desks", "rooms", "people", "other"] as const;

export type MapTab = (typeof MAP_TABS)[number];

/** One tab's position (its index in the array) and visibility. */
export type MapTabSetting = {
  tab: MapTab;
  enabled: boolean;
};

/**
 * Organization-chosen order and visibility of the map side-panel tabs
 * (map_tabs_settings). The API always answers with a full list, falling back
 * to the product default for organizations that never configured it.
 */
export type MapTabsSettings = {
  tabs?: MapTabSetting[];
};

/**
 * Which sections the mobile app's home screen shows (mobile_home_settings).
 * The API answers with every flag, all true for organizations that never
 * configured it; a partial update only changes the flags it sends.
 */
export type MobileHomeSettings = {
  show_reservations: boolean;
  show_people_in_office: boolean;
  show_quick_actions: boolean;
};

export type OrganizationSettings = OfficeOrOrgSettings &
  OrganizationReservationsSettings & {
    week_starts_on?: WeekStartsOnDay;
    enable_privacy_mode?: boolean;
    privacy_mode_offices?: string[];
    enable_rooms_privacy?: boolean;
    treat_reservation_created_users_as_guests?: boolean;
    calendar_settings?: {
      microsoft_enable_delegated_user_access: boolean;
      microsoft_online_meeting_providers:
        | []
        | [OnlineMeetingIntegrations.MICROSOFT_ONLINE_MEETING_PROVIDERS];
      google_online_meeting_providers:
        | []
        | [OnlineMeetingIntegrations.GOOGLE_ONLINE_MEETING_PROVIDERS];
    };
    theme_settings?: {
      colors?: Record<string, string>;
      font?: string;
    };
    map_theme_settings?: MapSpotColorsSettings;
    map_tabs_settings?: MapTabsSettings;
    mobile_home_settings?: Partial<MobileHomeSettings>;
    design_system_version?: 'v1' | 'v2';
    mobile_app_version?: 'v1' | 'v2';
    reservations_settings?: OrganizationReservationsSettings;
    feature_flags?: {
        workplace_requests: boolean
        tessa: boolean
        visitor_management: boolean
        room_displays: boolean
        wayfinder_displays: boolean
        timeline: boolean
        map: boolean
        dashboard: boolean
        reporting: boolean
        mcp?: boolean
    },
  };

export type OrganizationReservationsSettings = {
  allow_cross_office_overlapping?: boolean;
  allowed_cross_office_overlapping_types?: string[];
};

export type AccountSettings = {
  billing_notification_enabled: boolean;
};

export type WebhookSigningSecret = {
  secret: string;
  activates: string;
};

export type WebhookSigningSecrets = {
  current: WebhookSigningSecret;
  pending: WebhookSigningSecret | null;
};

/** Events an organization webhook endpoint can subscribe to. */
export const WEBHOOK_EVENT_TYPES = [
  "ticket.created",
  "ticket.updated",
  "ticket.deleted",
  "ticket.approved",
  "ticket.rejected",
  "ticket.comment.created",
  "ticket.comment.updated",
  "ticket.comment.deleted"
] as const;

export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

export type WebhookEndpoint = {
  id: string;
  url: string;
  description: string | null;
  event_types: WebhookEventType[];
  /** Disabled endpoints receive no events but can still be pinged. */
  enabled: boolean;
  created: string;
  updated: string;
};

export type WebhookEndpointCreateRequest = {
  url: string;
  description?: string;
  event_types: WebhookEventType[];
  enabled?: boolean;
};

/** Partial update: omitted fields are left unchanged, an empty description clears it. */
export type WebhookEndpointUpdateRequest = Partial<{
  url: string;
  description: string;
  event_types: WebhookEventType[];
  enabled: boolean;
}>;

export type WebhookDeliveryStatus = "pending" | "delivered" | "failed";

export type WebhookDeliverySummary = {
  id: string;
  /** Shared by every delivery of the same event, including redeliveries. */
  event_id: string;
  /** A subscribable event type, or `ping`. */
  event_type: string;
  status: WebhookDeliveryStatus;
  attempt_count: number;
  last_error: string | null;
  created: string;
  delivered: string | null;
  /** When the next attempt runs; null once delivered or failed. */
  next_attempt: string | null;
};

export type WebhookDeliveryAttempt = {
  attempt: number;
  at: string;
  /** Null when no response was received. */
  http_status: number | null;
  /** The first 2048 bytes of the response body. */
  response_body: string;
  error: string | null;
  duration_ms: number;
};

export type WebhookDelivery = WebhookDeliverySummary & {
  url: string;
  /** The exact signed request body, a JSON envelope serialized as a string. */
  body: string;
  attempts: WebhookDeliveryAttempt[];
};

export type ListWebhookDeliveriesParams = {
  cursor?: string;
  limit?: number;
  /** Empty or omitted means every status. */
  status?: WebhookDeliveryStatus[];
};
